import { message } from "antd";
import { useCallback, useReducer, useTransition } from "react";

export const DEFAULT_COL_WIDTH = 100;
export const DEFAULT_ROWS = 30;
export const DEFAULT_COLS = 26;

export const colLetter = (i) => {
  let s = "";
  i++;
  while (i > 0) {
    i--;
    s = String.fromCharCode(65 + (i % 26)) + s;
    i = Math.floor(i / 26);
  }
  return s;
};
export const isNumeric = (v) =>
  v !== "" && v !== null && v !== undefined && !isNaN(Number(v));

export function makeDefaultCols(n) {
  return Array.from({ length: n }, (_, i) => ({
    key: colLetter(i),
    width: DEFAULT_COL_WIDTH,
  }));
}
export function makeDefaultData(rows, cols) {
  return Array.from({ length: rows }, () => Array(cols).fill(""));
}

// ─── Editor Reducer (merged UPDATE_VALUE + UPDATE_FORMULA → SET_VALUE) ───────
export const INITIAL_EDITOR_STATE = { cell: null, value: "", formula: "" };

export const editorReducer = (state, action) => {
  switch (action.type) {
    case "START_EDIT":
      return {
        cell: { r: action.r, c: action.c },
        value: action.value ?? "",
        formula: action.value ?? "",
      };
    case "SET_VALUE": // replaces both UPDATE_VALUE and UPDATE_FORMULA
      return { ...state, value: action.value, formula: action.value };
    case "CANCEL":
    case "COMMIT":
      return INITIAL_EDITOR_STATE;
    default:
      return state;
  }
};

// ─── UI Reducer ──────────────────────────────────────────────────────────────
export const INITIAL_UI_STATE = {
  selected: { r: 0, c: 0 },
  selection: null,
  ctxMenu: null,
  resizingCol: null,
  selecting: false,
};

export const uiReducer = (state, action) => {
  switch (action.type) {
    case "SELECT_CELL":
      return {
        ...state,
        selected: { r: action.r, c: action.c },
        selection: null,
      };
    case "SET_GRID": {
      return {
        data: action.data,
        cols: makeDefaultCols(action.colCount),
      };
    }
    case "SELECT_RANGE":
      return {
        ...state,
        selection: {
          r1: action.r1,
          c1: action.c1,
          r2: action.r2,
          c2: action.c2,
        },
      };
    case "SET_CONTEXT_MENU":
      return { ...state, ctxMenu: action.menu };
    case "CLEAR_CONTEXT_MENU":
      return { ...state, ctxMenu: null };
    case "START_RESIZE":
      return { ...state, resizingCol: action.colIdx };
    case "END_RESIZE":
      return { ...state, resizingCol: null };
    case "SET_SELECTING":
      return { ...state, selecting: action.selecting };
    default:
      return state;
  }
};

// ─── Grid Reducer (replaces useState setData/setCols) ────────────────────────
const gridReducer = (state, action) => {
  const { data, cols } = state;

  switch (action.type) {
    case "SET_GRID": {
      return {
        ...state,
        data: action.data,
        cols: makeDefaultCols(action.colCount),
      };
    }
    case "SET_CELL": {
      const { r, c, value } = action;
      return {
        ...state,
        data: data.map((row, ri) =>
          ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : row,
        ),
      };
    }
    case "CLEAR_RANGE": {
      const { r1, c1, r2, c2 } = action;
      return {
        ...state,
        data: data.map((row, ri) =>
          ri >= r1 && ri <= r2
            ? row.map((cell, ci) => (ci >= c1 && ci <= c2 ? "" : cell))
            : row,
        ),
      };
    }
    case "ADD_ROW":
      return { ...state, data: [...data, Array(cols.length).fill("")] };
    case "ADD_COL": {
      const idx = cols.length;
      return {
        cols: [...cols, { key: colLetter(idx), width: DEFAULT_COL_WIDTH }],
        data: data.map((row) => [...row, ""]),
      };
    }
    case "INSERT_ROW": {
      const nd = [...data];
      nd.splice(action.at, 0, Array(cols.length).fill(""));
      return { ...state, data: nd };
    }
    case "INSERT_COL": {
      const nc = [...cols];
      nc.splice(action.at, 0, { key: "_", width: DEFAULT_COL_WIDTH });
      return {
        cols: nc.map((col, i) => ({ ...col, key: colLetter(i) })),
        data: data.map((row) => {
          const nr = [...row];
          nr.splice(action.at, 0, "");
          return nr;
        }),
      };
    }
    case "DELETE_ROW":
      return { ...state, data: data.filter((_, i) => i !== action.at) };
    case "DELETE_COL":
      return {
        cols: cols
          .filter((_, i) => i !== action.at)
          .map((col, i) => ({ ...col, key: colLetter(i) })),
        data: data.map((row) => row.filter((_, i) => i !== action.at)),
      };
    case "SET_COL_WIDTH":
      return {
        ...state,
        cols: cols.map((col, i) =>
          i === action.colIdx ? { ...col, width: action.width } : col,
        ),
      };
    case "SORT_COL": {
      const sorted = [...data].sort((a, b) => {
        const av = a[action.c] ?? "",
          bv = b[action.c] ?? "";
        if (isNumeric(av) && isNumeric(bv))
          return action.asc ? Number(av) - Number(bv) : Number(bv) - Number(av);
        return action.asc
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
      return { ...state, data: sorted };
    }
    default:
      return state;
  }
};

// ─── History wrapper (undo/redo, replaces raw useState) ──────────────────────
function historyReducer(state, action) {
  if (action.type === "UNDO") {
    if (!state.past.length) return state;
    return {
      past: state.past.slice(0, -1),
      present: state.past.at(-1),
      future: [state.present, ...state.future],
    };
  }
  if (action.type === "REDO") {
    if (!state.future.length) return state;
    return {
      past: [...state.past, state.present],
      present: state.future[0],
      future: state.future.slice(1),
    };
  }
  const next = gridReducer(state.present, action);
  if (next === state.present) return state;
  return {
    past: [...state.past, state.present].slice(-50),
    present: next,
    future: [],
  };
}

export const useDataManagement = (
  initialRows = DEFAULT_ROWS,
  initialCols = DEFAULT_COLS,
) => {
  const [state, dispatch] = useReducer(historyReducer, null, () => ({
    past: [],
    present: {
      data: makeDefaultData(initialRows, initialCols),
      cols: makeDefaultCols(initialCols),
    },
    future: [],
  }));

  const [isPending, startTransition] = useTransition();

  const gridDispatch = useCallback(
    (action) => startTransition(() => dispatch(action)),
    [],
  );

  return {
    data: state.present.data,
    cols: state.present.cols,
    isPending,
    dispatch: gridDispatch, // single dispatch replaces setData + setCols
    undo: useCallback(() => dispatch({ type: "UNDO" }), []),
    redo: useCallback(() => dispatch({ type: "REDO" }), []),
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const normalizeSelection = (sel) =>
  sel
    ? {
        r1: Math.min(sel.r1, sel.r2),
        c1: Math.min(sel.c1, sel.c2),
        r2: Math.max(sel.r1, sel.r2),
        c2: Math.max(sel.c1, sel.c2),
      }
    : null;

export const isInSelection = (r, c, selection) => {
  if (!selection) return false;
  const n = normalizeSelection(selection);
  return r >= n.r1 && r <= n.r2 && c >= n.c1 && c <= n.c2;
};

export const getSelectedCells = (data, selected, selection) => {
  const norm = normalizeSelection(selection);
  if (!norm) return [data[selected.r]?.[selected.c] ?? ""];
  const cells = [];
  for (let r = norm.r1; r <= norm.r2; r++)
    for (let c = norm.c1; c <= norm.c2; c++) cells.push(data[r]?.[c] ?? "");
  return cells;
};

export const useShowToast = () =>
  useCallback((msg) => message.success(msg), []);
